import * as Sentry from "@sentry/react";
import {getNode} from "../getNode";
import type {DibDocument, DocgenDataError} from "../types";

/**
 * Element kinds dokumentgenerator renders from their object: mirrors its
 * rules.tsx (see CLAUDE.md here). Without the object, list and comment
 * silently drop content; the others throw.
 */
const NEEDS_OBJECT = new Set([
    "dib-x-var",
    "dib-x-var-n",
    "dib-x-alternatives",
    "dib-x-alternative",
    "dib-x-letterhead",
    "dib-x-optional",
    "dib-x-list",
    "dib-x-comment",
]);

/**
 * The elements the generator would render that lack the object their kind
 * needs: none stored under the element id, or a pointer to a variable that
 * doesn't exist. Walks from where the generator's view starts and skips
 * hidden subtrees, as renderNode does, and resolves nodes with the render
 * engine's own getNode, so it flags exactly what the generator would hit.
 */
export function findDocgenDataErrors(document: DibDocument): DocgenDataError[] {
    const errors: DocgenDataError[] = [];
    const visit = (id: string) => {
        const {kind, element, object} = getNode(document, id);
        if (!element || element.attributes?.class?.includes("dib-x-hide")) {
            return;
        }
        if (kind && NEEDS_OBJECT.has(kind) && !object) {
            errors.push({
                elementId: id,
                kind,
                dataVarId: element.attributes["data-var-id"],
            });
        }
        for (const child of element.children ?? []) {
            if ("id" in child) visit(child.id);
        }
    };
    const start = document.segmentId || document.root[0]?.id;
    if (start) visit(start);
    return errors;
}

/**
 * Sends a document's data errors to Sentry. Called once per fetch, from
 * getDocgenDocument's onQueryStarted. The route is on every event already
 * (request.url). Does nothing without errors.
 */
export function reportDocgenDataErrors(
    document: DibDocument,
    errors: DocgenDataError[]
) {
    if (errors.length === 0) return;

    Sentry.captureMessage("Docgen document has elements without their object", {
        level: "error",
        tags: {
            feature: "content.docgen",
            topicId: document.topicId,
            resourceId: document.id,
        },
        // In extra, not contexts: both are normalized to a depth of 3, which
        // keeps the entries' fields here (see documentParts.ts).
        extra: {elements: errors},
    });
}
